import React from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { AppNotification, NotificationType } from '../types.ts';
import { 
  Bell, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  AlertCircle, 
  X, 
  Check, 
  Trash2,
  ExternalLink
} from 'lucide-react';
import { formatDate } from './AssetModal.tsx';

interface NotificationCenterProps {
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose }) => {
  const { notifications, markNotificationRead, markAllNotificationsRead, clearNotifications } = useStore();
  
  const unreadCount = notifications.filter(n => !n.read).length;

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success': return <CheckCircle size={18} className="text-green-500" />;
      case 'warning': return <AlertTriangle size={18} className="text-yellow-500" />;
      case 'error': return <AlertCircle size={18} className="text-red-500" />;
      default: return <Info size={18} className="text-blue-500" />;
    }
  };

  const getBgColor = (type: NotificationType) => {
    switch (type) {
      case 'success': return 'bg-green-500/10 border-green-500/20';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/20';
      case 'error': return 'bg-red-500/10 border-red-500/20';
      default: return 'bg-blue-500/10 border-blue-500/20';
    }
  };

  return (
    <div className="absolute bottom-16 left-0 w-[360px] md:w-[400px] max-w-[calc(100vw-2rem)] bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-xl shadow-2xl overflow-hidden z-[100] animate-slideUp">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
         <div className="flex items-center gap-2">
            <h3 className="font-bold text-[var(--text-primary)]">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-[var(--accent-primary)] text-white text-[10px] font-bold rounded-full">
                {unreadCount} new
              </span>
            )}
         </div>
         <div className="flex items-center gap-1">
            {unreadCount > 0 && (
               <button 
                 onClick={markAllNotificationsRead}
                 className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                 title="Mark all as read"
               >
                 <Check size={16} />
               </button>
            )}
            {notifications.length > 0 && (
              <button 
                onClick={clearNotifications}
                className="p-1.5 text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Clear all"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-1.5 text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors md:hidden"
            >
              <X size={16} />
            </button>
         </div>
      </div>

      {/* List */}
      <div className="max-h-[400px] overflow-y-auto p-2 space-y-2 bg-[var(--bg-tertiary)]/20">
         {notifications.length > 0 ? (
           notifications.map(item => (
             <div 
               key={item.id}
               className={`relative p-3 rounded-lg border transition-all ${item.read ? 'bg-[var(--bg-primary)] border-[var(--border-subtle)]' : `${getBgColor(item.type)} shadow-sm`}`}
             >
                <div className="flex gap-3 items-start">
                   <div className="flex-shrink-0 mt-0.5">
                     {getIcon(item.type)}
                   </div>
                   <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                         <h4 className={`text-sm font-semibold truncate pr-4 ${item.read ? 'text-[var(--text-primary)]' : 'text-black dark:text-white'}`}>{item.title}</h4>
                         {!item.read && (
                           <button 
                             onClick={() => markNotificationRead(item.id)}
                             className="text-[var(--accent-primary)] hover:text-[var(--accent-primary-hover)] transition-colors"
                             title="Mark as read"
                           >
                              <div className="w-2 h-2 rounded-full bg-[var(--accent-primary)]"></div>
                           </button>
                         )}
                      </div>
                      <p className={`text-xs mt-1 leading-relaxed ${item.read ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)] opacity-90'}`}>
                        {item.message}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-black/5 dark:border-white/5">
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          {formatDate(item.timestamp)}
                        </span>
                        {item.link && (
                          <a href={item.link} className="flex items-center gap-1 text-[10px] font-medium text-[var(--accent-primary)] hover:underline">
                            View <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                   </div>
                </div>
             </div>
           ))
         ) : (
           <div className="flex flex-col items-center justify-center py-12 text-[var(--text-tertiary)]">
              <Bell size={32} className="opacity-20 mb-2" />
              <p className="text-sm">No notifications yet.</p>
           </div>
         )}
      </div>
    </div>
  );
};

export default NotificationCenter;